import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { photoUrl } from "../lib/api";

export default function PhotoLightbox({ photos, index, onClose }) {
  const [current, setCurrent] = useState(index ?? 0);
  const open = index != null;

  useEffect(() => {
    if (index != null) setCurrent(index);
  }, [index]);

  const prev = useCallback(() => setCurrent((c) => (c - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setCurrent((c) => (c + 1) % photos.length), [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  if (!open || !photos.length) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-5xl bg-card border-border p-4 [&>button]:hidden"
        data-testid="photo-lightbox"
      >
        <DialogTitle className="sr-only">Photo preview</DialogTitle>
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-muted-foreground" data-testid="lightbox-counter">
            {current + 1} / {photos.length}
          </span>
          <button data-testid="lightbox-close-btn" onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors duration-150">
            <X size={20} />
          </button>
        </div>
        <div className="relative flex items-center justify-center bg-black/30 border border-border min-h-[300px]">
          {photos.length > 1 && (
            <button data-testid="lightbox-prev-btn" onClick={prev}
              className="absolute left-2 z-10 p-2 bg-black/50 text-white hover:text-[hsl(var(--primary))] transition-colors duration-150 rounded-full">
              <CaretLeft size={22} weight="bold" />
            </button>
          )}
          <img
            src={photoUrl(photos[current])}
            alt={`trade attachment ${current + 1}`}
            className="max-h-[70vh] max-w-full object-contain"
            data-testid="lightbox-image"
          />
          {photos.length > 1 && (
            <button data-testid="lightbox-next-btn" onClick={next}
              className="absolute right-2 z-10 p-2 bg-black/50 text-white hover:text-[hsl(var(--primary))] transition-colors duration-150 rounded-full">
              <CaretRight size={22} weight="bold" />
            </button>
          )}
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 justify-center overflow-x-auto py-1">
            {photos.map((pid, i) => (
              <button key={pid} data-testid={`lightbox-thumb-${i}`} onClick={() => setCurrent(i)}
                className={`border shrink-0 ${i === current ? "border-[hsl(var(--primary))]" : "border-border opacity-60 hover:opacity-100"}`}>
                <img src={photoUrl(pid)} alt={`thumbnail ${i + 1}`} className="h-14 w-20 object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
